import React, { useRef, useEffect, useMemo, forwardRef, useImperativeHandle, useCallback } from "react"
import { useSprings, animated } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useListenCallback } from "@hooks/useListenCallback";
import cl from 'classnames';
import css from './Slider.module.scss';

function clamp(min: number, num: number, max: number) {
    return num <= min ? min : num >= max ? max : num;
}

const useElementsSizes = (ref: React.RefObject<HTMLElement>, size: number) => {
    const [scrollWidth, setScrollWidth] = React.useState(0);
    const [sliderWidth, setSliderWidth] = React.useState(0);
    const [slideWidth, setSlideWidth] = React.useState(0);
    const [gap, setGap] = React.useState(0);
    useEffect(() => {
        if(!ref.current) return;

        const computedStyle = getComputedStyle(ref.current);
        const gap = parseFloat(computedStyle.gap);
        setGap(gap);

        const firstChild = ref.current?.children[0] as HTMLElement;
        if(firstChild) {
            setSlideWidth(firstChild.offsetWidth);
        }
        setScrollWidth(ref.current.scrollWidth);
    }, [size]);
    useListenCallback("resize", () => {
        setSliderWidth(ref.current?.offsetWidth ?? 0);
    },[size]);
    return { scrollWidth, sliderWidth, slideWidth, gap };
}

type Props = {
    children: React.ReactNode;
    idx?: number;
    className?: string;
    slideClassName?: string;
    testId?: string;
    handleIndex?: (index: number) => void;
    withButtons?: boolean;
}

const BorderStretchCoefficient = 0.5;

const Slider = ({
    children,
    idx = 0,
    className,
    slideClassName,
    testId,
    handleIndex,
    withButtons = false
}: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const size = useMemo( () => React.Children.count(children), [children]);
    const { scrollWidth, sliderWidth, slideWidth, gap } = useElementsSizes(ref, size);
    const slideWithGapWidth = slideWidth + gap;
    const [slideIdx, setSlideIdx] = React.useState(0);
    const currentX = useRef(0);
    const [scrollX, setScrollX] = React.useState(0);

    const handleIndexChange = useCallback((index: number) => {
        setSlideIdx(index);
        handleIndex?.(index);
    }, [handleIndex, setSlideIdx]);

    useEffect(() => {
        if(idx === slideIdx) return;
        handleIndexChange(idx);
        currentX.current = animateToPosition(-(idx * slideWithGapWidth));
    }, [idx]);

    const [props, api] = useSprings(size, i => ({
        x: 0,
    }));

    /**
     * Update position of the slider
     * @return {number} new position if it was truncated or same x if not
     */
    const animateToPosition = useCallback((x: number, isElastic: boolean = false): number => {
        if(!ref.current) return 0;

        const elastisity = isElastic ? Math.ceil(slideWidth * BorderStretchCoefficient) : 0;
        let invertX = -x; // because we are using negative x, with inverted X easier to read the code
        const leftBorder = 0 - elastisity;
        const rightBorder = scrollWidth - sliderWidth + elastisity;
        invertX = clamp(leftBorder, invertX, rightBorder);
        
        api.start(i => ({
            x: -invertX,
        }))
        return -invertX;
    }, [api, scrollWidth]);

    const isPreviousEnabled = scrollX < 0;
    const isNextEnabled = scrollWidth - sliderWidth > - scrollX;
    const handlePrev = useCallback(() => {
        const newIdx = Math.max(slideIdx - 1, 0);
        currentX.current = animateToPosition(-(newIdx * slideWithGapWidth));
        setScrollX(currentX.current);
        handleIndexChange(newIdx);
    }, [animateToPosition, slideIdx]);
    const handleNext = useCallback(() => {
        const newIdx = Math.min(slideIdx + 1, size - 1);
        currentX.current = animateToPosition(-(newIdx * slideWithGapWidth));
        setScrollX(currentX.current);
        handleIndexChange(newIdx);
    }, [animateToPosition, slideIdx]);


    const ticking = useRef(false);
    const handleWheel: React.WheelEventHandler<HTMLDivElement> = useCallback((event) => {
        if (!ticking.current && event.deltaX !== 0) {
            window.requestAnimationFrame(() => {
                currentX.current = animateToPosition(currentX.current - event.deltaX);
                const isStickToLast = -currentX.current > scrollWidth - sliderWidth;
                const newIdx = isStickToLast ? size - 1 : Math.round(-currentX.current / slideWithGapWidth);
                setScrollX(currentX.current);
                handleIndexChange(newIdx);
                ticking.current = false;
            });
            ticking.current = true;
        }
    }, [animateToPosition, sliderWidth, slideWithGapWidth]);

    const isDragging = useRef(false);
    const bind = useDrag(({ active, movement: [mx], cancel }) => {
        const offset = currentX.current + mx;
        let x = currentX.current + mx
        isDragging.current = active;
        if(!active) {
            let currentSlideIdx = Math.round(-offset / slideWithGapWidth);
            currentSlideIdx = clamp(0, currentSlideIdx, size - 1);
            x = - currentSlideIdx * slideWithGapWidth;
            currentX.current = animateToPosition(x);
            setScrollX(currentX.current);
            handleIndexChange(currentSlideIdx);
            return;
        }
        const correctedX = animateToPosition(x, true);
        const isXisTruncated = correctedX !== x;
        if(isXisTruncated) {
            setScrollX(correctedX);
            isDragging.current = false;
            cancel();
        }
    }, {
        axis: 'x'
    });

    const handleFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        setTimeout(() => {
            if(isDragging.current) return;
            const focusedElement = event.target;
            const parentElement = focusedElement.closest(`.${css.item}`);
    
            if (parentElement) {
                const parentElements = Array.from(ref.current?.children ?? []);
                const itemIndex = parentElements.indexOf(parentElement);
                const newX = animateToPosition(-itemIndex * slideWithGapWidth);
                setScrollX(newX);
                handleIndexChange(itemIndex);
            }
        }, 70); // delay to prevent focus event from being triggered by click during dragging
    }, [size, slideWithGapWidth, handleIndex]);
    return (<div className={css.container} {...(!!testId ? { 'data-testid': testId } : {})}>
        {withButtons && <button className={cl(css.swipeButton, css.prev)} disabled={!isPreviousEnabled} onClick={handlePrev}><Icon icon={faChevronLeft} /></button>}
        <div ref={ref}
            className={cl(className, css.slider)}
            style={{ userSelect: 'none' }}
            data-testid={!!testId ? `${testId}-slider` : 'swapSlider'}
            onWheel={handleWheel}
            onFocus={handleFocus}
            >
            {props.map(({ x }, i) => (
                <animated.div className={cl(slideClassName, css.item)} {...bind()} key={i} style={{ x }}>
                    {React.Children.toArray(children)[i]}
                </animated.div>
            ))}
        </div>
        {withButtons && <button onClick={handleNext} disabled={!isNextEnabled} className={cl(css.swipeButton, css.next)}><Icon icon={faChevronRight} /></button>}
    </div>);
}

export default Slider;